import { clearManualFootageStorage } from "./manualFootageStorage";
import { clientStore } from "./clientStorage";
import { utf8Bytes } from "./persistenceRegistry";
import { walkthroughSessionStore } from "./walkthroughSessionStore";

export const PERSISTENCE_LOGOUT_KEY = "ln:persistence:logout";
export const PERSISTENCE_LOCAL_CLEAR_KEY = "ln:persistence:local-clear";
export const ACTIVE_PERSISTENCE_USER_KEY = "ln:persistence:active-user";
export const persistenceGenerationKey = (userId: string | null) =>
  `ln:persistence:generation:${userId ?? "anon"}`;
const PERSISTENCE_EVENT_MAX_BYTES = 4 * 1024;

export interface PersistenceCoordinationEvent {
  userId: string | null;
  generation: number;
  at: number;
}

export function readPersistenceEvent(
  raw: string | null,
  kind: "local-clear" | "logout"
): PersistenceCoordinationEvent | null {
  if (!raw) return null;
  const failureKey = `coordination:${kind}:invalid`;
  if (utf8Bytes(raw) > PERSISTENCE_EVENT_MAX_BYTES) {
    clientStore.reportMemoryOnly("invalid", failureKey);
    return null;
  }
  try {
    const event = JSON.parse(raw) as Partial<PersistenceCoordinationEvent>;
    const userIdValid = kind === "logout"
      ? event.userId === null ||
        (typeof event.userId === "string" && event.userId.length > 0)
      : typeof event.userId === "string" && event.userId.length > 0;
    if (
      !userIdValid ||
      !Number.isSafeInteger(event.generation) ||
      (event.generation as number) < 0 ||
      !Number.isFinite(event.at) ||
      (event.at as number) <= 0 ||
      (event.at as number) > Date.now() + 5 * 60 * 1000
    ) {
      clientStore.reportMemoryOnly("invalid", failureKey);
      return null;
    }
    clientStore.clearDurabilityFailures((key) => key === failureKey);
    return event as PersistenceCoordinationEvent;
  } catch {
    clientStore.reportMemoryOnly("invalid", failureKey);
    return null;
  }
}

const readGeneration = (
  userId: string | null
): { value: number; valid: boolean } => {
  const key = persistenceGenerationKey(userId);
  const raw = clientStore.getItem(key);
  if (raw === null) return { value: 0, valid: true };
  if (raw.length > 32 || !/^\d+$/.test(raw)) {
    clientStore.reportMemoryOnly("invalid", key);
    return { value: 0, valid: false };
  }
  const value = Number(raw);
  if (!Number.isSafeInteger(value) || value < 0) {
    clientStore.reportMemoryOnly("invalid", key);
    return { value: 0, valid: false };
  }
  clientStore.clearDurabilityFailures((failureKey) => failureKey === key);
  return { value, valid: true };
};

export function getPersistenceGeneration(userId: string | null): number {
  const generation = readGeneration(userId);
  return generation.valid ? generation.value : Number.NaN;
}

export function advancePersistenceGeneration(userId: string | null): number {
  const current = readGeneration(userId);
  const nextGeneration = current.valid
    ? current.value + 1
    : Math.max(1, Date.now());
  clientStore.setItem(persistenceGenerationKey(userId), String(nextGeneration));
  return nextGeneration;
}

export function markPersistenceSignedIn(userId: string): void {
  if (getActivePersistenceUser() === userId) return;
  clientStore.setItem(ACTIVE_PERSISTENCE_USER_KEY, userId);
}

export function transitionPersistenceAuth(
  previousUserId: string | null,
  nextUserId: string | null
): string | null {
  if (nextUserId) {
    if (previousUserId === nextUserId && getActivePersistenceUser() === nextUserId) return nextUserId;
    if (previousUserId && previousUserId !== nextUserId) clearUserPersistence(previousUserId);
    markPersistenceSignedIn(nextUserId);
    return nextUserId;
  }
  const previous = previousUserId ?? getActivePersistenceUser();
  if (previous) clearUserPersistence(previous);
  return null;
}

export const getActivePersistenceUser = (): string | null => {
  const raw = clientStore.getItem(ACTIVE_PERSISTENCE_USER_KEY);
  if (raw === null) return null;
  if (
    raw.length === 0 || raw.length > 256 ||
    /[\u0000-\u001f\u007f]/.test(raw)
  ) {
    clientStore.reportMemoryOnly("invalid", ACTIVE_PERSISTENCE_USER_KEY);
    return null;
  }
  clientStore.clearDurabilityFailures(
    (key) => key === ACTIVE_PERSISTENCE_USER_KEY
  );
  return raw;
};

export function persistenceWriteAllowed(userId: string | null, generation: number): boolean {
  const current = readGeneration(userId);
  return Boolean(userId) && current.valid &&
    getActivePersistenceUser() === userId && current.value === generation;
}

/**
 * True when this generation was created by an explicit clear/logout. Legacy
 * envelopes without an owner generation must never cross that boundary.
 */
export function hasPersistenceInvalidationFence(
  userId: string,
  generation = getPersistenceGeneration(userId)
): boolean {
  // The per-user generation is durable, cannot be overwritten by another
  // user's event, and is the authoritative proof that some invalidation has
  // already happened. Ownerless legacy values are safe to adopt only at the
  // untouched generation zero.
  const current = readGeneration(userId);
  if (!current.valid || generation > 0) return true;
  for (const [key, kind] of [
    [PERSISTENCE_LOCAL_CLEAR_KEY, "local-clear"],
    [PERSISTENCE_LOGOUT_KEY, "logout"],
  ] as const) {
    const raw = clientStore.getItem(key);
    const event = readPersistenceEvent(raw, kind);
    if (raw && !event) return true;
    if (event?.userId === userId && event.generation === generation) return true;
  }
  return false;
}

/**
 * Logout advances the durable owner generation and best-effort removes known
 * namespaced payloads. Only explicit Clear Local Data claims verified physical
 * deletion; the registry labels this boundary `logout-invalidation`.
 */
export function clearUserPersistence(userId: string | null): void {
  const nextGeneration = advancePersistenceGeneration(userId);
  clearManualFootageStorage(userId);
  if (userId) {
    clientStore.removeByPrefix(`search_draft:${userId}:`);
    clientStore.removeByPrefix(`search_result:${userId}:`);
    clientStore.removeByPrefix(`persist:${userId}:`);
    clientStore.removeByPrefix(`scroll:${userId}:`);
  } else {
    clientStore.removeByPrefix("search_draft:anon:");
    clientStore.removeByPrefix("persist:anon:");
  }
  if (!userId || getActivePersistenceUser() === userId) {
    clientStore.removeItem(ACTIVE_PERSISTENCE_USER_KEY);
  }
  walkthroughSessionStore.clear();
  clientStore.setItem(PERSISTENCE_LOGOUT_KEY, JSON.stringify({
    userId, generation: nextGeneration, at: Date.now(),
  }));
}
